import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '@zerops/zerops/env';
import type { RecipeHandoffPayload } from './recipe-handoff-base.action';

interface BackendApiResponse<T> {
  success: boolean;
  data?: T;
  error?: { code: string; message: string };
}

@Injectable({ providedIn: 'root' })
export class RecipeHandoffBaseApi {
  #httpClient = inject(HttpClient);
  #endpoint = `${environment.webApiUrl}/api/recipe-handoff`;

  /**
   * POST a recipe + zagent payload, get back an opaque single-use UUID
   * (30-min TTL). Used to carry zagent config across the OAuth full-page
   * round-trip — embedded into the OAuth state so the post-auth callback
   * can consume it. Email path on the in-app /recipes also uses this for
   * consistency, even though NgRx state would survive there.
   */
  create$(payload: RecipeHandoffPayload): Observable<string> {
    return this.#httpClient
      .post<BackendApiResponse<{ id: string; expiresAt: string }>>(
        this.#endpoint,
        payload,
        { headers: { zefSkipAuthHeader: 'true' } }
      )
      .pipe(
        map((response) => {
          if (!response.success || !response.data?.id) {
            throw new Error(response.error?.message ?? 'Recipe handoff create failed');
          }
          return response.data.id;
        })
      );
  }

  /**
   * Single-use. Backend atomically deletes on read, so calling twice with the
   * same id will 404 the second time.
   */
  consume$(id: string): Observable<RecipeHandoffPayload> {
    return this.#httpClient
      .get<BackendApiResponse<RecipeHandoffPayload>>(
        `${this.#endpoint}/${encodeURIComponent(id)}`,
        { headers: { zefSkipAuthHeader: 'true' } }
      )
      .pipe(
        map((response) => {
          if (!response.success || !response.data) {
            throw new Error(response.error?.message ?? 'Recipe handoff consume failed');
          }
          return response.data;
        })
      );
  }
}
